import { CopyOutlined } from "@ant-design/icons";
import { Flex, Form, Input, Modal, theme } from "antd";
import { useTranslation } from "react-i18next";
import { useDuplicateChartMutation } from "../../graphql/mutation.duplicateChart";
import { Chart } from "../../graphql/query.charts";

type DuplicateFormValues = {
  newChartTitle: string;
};

type DuplicateChartModalProps = {
  chart: Chart;
  onSuccess: (newChart: Chart) => void;
  onCancel: () => void;
  onError: (error: unknown) => void;
};

export const DuplicateChartModal = ({
  chart,
  onSuccess,
  onError,
  onCancel,
}: DuplicateChartModalProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const [duplicateForm] = Form.useForm();
  const newChartTitle = Form.useWatch("newChartTitle", duplicateForm);
  const [runDuplicateChart, { loading: isDuplicateChartLoading }] =
    useDuplicateChartMutation();

  const handleDuplicateChart = async (
    chart: Chart,
    values: DuplicateFormValues
  ) => {
    try {
      const response = await runDuplicateChart({
        variables: {
          input: {
            chartID: chart.id,
            title: values.newChartTitle.trim(),
          },
        },
      });
      if (response.data) {
        onSuccess(response.data.duplicateChart.newChart);
      } else {
        throw new Error("Chart duplication failed");
      }
    } catch (error: unknown) {
      onError(error);
    }
  };

  return (
    <Modal
      title={
        <Flex gap={8}>
          <CopyOutlined /> {t("modal_duplicate_title")}
        </Flex>
      }
      open={true}
      confirmLoading={isDuplicateChartLoading}
      okText={t("duplicate")}
      cancelText={t("cancel")}
      cancelButtonProps={{
        disabled: isDuplicateChartLoading,
      }}
      okButtonProps={{
        autoFocus: true,
        htmlType: "submit",
        disabled: newChartTitle === "",
      }}
      onOk={duplicateForm.submit}
      centered={true}
      onCancel={onCancel}
    >
      <Flex vertical gap={token.marginSM}>
        <Form
          form={duplicateForm}
          layout="vertical"
          name="basic"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          initialValues={{
            newChartTitle: `${chart.title} (${t("copied_suffix")})`,
          }}
          onFinish={(values) => handleDuplicateChart(chart, values)}
          autoComplete="off"
        >
          <Form.Item<DuplicateFormValues>
            label={t("modal_duplicate_content")}
            name="newChartTitle"
            rules={[{ required: true, message: t("new_chart_title_required") }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Flex>
    </Modal>
  );
};
